import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ChevronRight, ChevronLeft, PlayCircle } from 'lucide-react';
import { toast } from 'sonner';

/* Guided demo tour — visitor-facing floating card in Restaurant OS demo mode.
   Explains what they see, why it matters and what they can do. ESC or "explore on my own" exits. */

const STEPS = [
  { to: '/restaurant', title: ['Home · Daily Brief', 'Home · Daily Brief'],
    es: 'Esto es lo primero que verías cada mañana: qué pasó ayer, por qué importa y qué hacer hoy. Baja y encontrarás las ideas de Rachel para vender más — puedes marcarlas como hechas.',
    en: 'This is what you would see every morning: what happened yesterday, why it matters and what to do today. Scroll down for Rachel\u2019s ideas to sell more — you can mark them done.' },
  { to: '/restaurant/revenue', title: ['Revenue', 'Revenue'],
    es: 'Tus ventas explicadas solas: tendencias, mejores días y platos estrella. Pasa el cursor por los gráficos y cambia los periodos — todo es interactivo.',
    en: 'Your sales explained by themselves: trends, best days, star dishes. Hover the charts and switch periods — everything is interactive.' },
  { to: '/restaurant/menu', title: ['Menu Intelligence', 'Menu Intelligence'],
    es: 'Tu menú deja de ser opinión y pasa a ser datos: qué subir de precio, qué promocionar y qué retirar — con las cifras delante.',
    en: 'Your menu stops being opinion and becomes data: what to reprice, promote or retire — with the numbers in front of you.' },
  { to: '/restaurant/reviews', title: ['Reviews', 'Reviews'],
    es: 'Tu reputación vigilada 24/7: la IA lee cada reseña, detecta patrones y te sugiere la respuesta con tu tono. Las estrellas también son ingresos.',
    en: 'Your reputation watched 24/7: the AI reads every review, spots patterns and drafts replies in your tone. Stars are revenue too.' },
  { to: '/restaurant/ai-executive', title: ['AI Executive', 'AI Executive'],
    es: 'El informe ejecutivo que un consultor cobraría caro — escrito por tu equipo de IA cada semana, con los datos reales del negocio.',
    en: 'The executive brief a consultant would charge a lot for — written by your AI team every week, from the business\u2019s real data.' },
];

const LOCATIONS_STEP = { to: '/restaurant/locations', title: ['Locales', 'Locations'],
  es: 'Todo el grupo en un vistazo: mapa de cobertura, ranking de locales y un comparador cara a cara. Toca un pin o un local del ranking para abrir su detalle.',
  en: 'The whole group at a glance: coverage map, location ranking and a head-to-head comparator. Tap a pin or a ranked location to open its detail.' };

const DONE_KEY = 'kx_rest_tour_done';
const API = process.env.REACT_APP_BACKEND_URL;
const MOBILE_STOPS = ['/restaurant', '/restaurant/revenue', '/restaurant/menu', '/restaurant/ai-executive'];

export function RestDemoTour({ isEs, multiLoc }) {
  const nav = useNavigate();
  const [isMobile] = useState(() => window.matchMedia('(max-width: 1023px)').matches);
  const allSteps = multiLoc ? [...STEPS.slice(0, 2), LOCATIONS_STEP, ...STEPS.slice(2)] : STEPS;
  const steps = isMobile ? MOBILE_STOPS.map((to) => allSteps.find((st) => st.to === to)) : allSteps;
  // Mobile: never cover the content — the tour waits as a discreet pill until invited.
  const [view, setView] = useState(() => (window.matchMedia('(max-width: 1023px)').matches ? 'min' : 'intro')); // intro | step | final | min
  const [i, setI] = useState(0);
  const [asked, setAsked] = useState(false);
  const [hidden, setHidden] = useState(() => {
    try { return localStorage.getItem(DONE_KEY) === '1'; } catch { return false; }
  });
  const dismiss = () => { setHidden(true); try { localStorage.setItem(DONE_KEY, '1'); } catch { /* noop */ } };

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') dismiss(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  if (hidden) return null;
  const go = (n) => {
    if (n >= steps.length) { setView('final'); return; }
    const next = Math.max(n, 0);
    setI(next);
    setView('step');
    nav(steps[next].to);
    window.scrollTo({ top: 0 });
  };
  const contactMe = async () => {
    setAsked(true);
    try {
      await fetch(`${API}/api/demo-access/profile`, { method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ contact_me: true }) });
      toast.success(isEs ? 'Perfecto — te contactamos muy pronto' : 'Perfect — we\u2019ll contact you very soon');
    } catch { /* noop */ }
    setTimeout(dismiss, 1200);
  };
  const s = steps[i];

  return (
    <div className="fixed bottom-20 lg:bottom-4 right-3 lg:right-4 z-[85] max-w-sm w-[calc(100vw-1.5rem)] sm:w-96" data-testid="rest-demo-tour">
      <AnimatePresence mode="wait">
        {view === 'min' ? (
          <motion.button key="min" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setView(i === 0 && !asked ? 'intro' : 'step')} data-testid="tour-reopen"
            style={{ background: 'radial-gradient(at 20% 15%, rgba(255,255,255,0.06), transparent 45%), linear-gradient(150deg, #101114, #1B1D22)' }}
            className="ml-auto flex items-center gap-2 rounded-full text-white px-4 py-2.5 shadow-2xl border border-white/15 text-[12.5px] font-bold hover:border-[#F77F11]/60 transition-colors">
            <PlayCircle className="w-4 h-4 text-[#F77F11]" /> {isEs ? 'Visita guiada' : 'Guided tour'}
          </motion.button>
        ) : (
          <motion.div key={view + i} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 16 }}
            style={{ background: 'radial-gradient(at 18% 12%, rgba(255,255,255,0.055), transparent 42%), radial-gradient(at 88% 92%, rgba(247,127,17,0.07), transparent 38%), linear-gradient(150deg, #0F1013, #1B1D22 60%, #121316)', backdropFilter: 'blur(14px)' }}
            className="rounded-2xl text-white shadow-2xl border border-white/15 overflow-hidden">
            {view === 'step' && (
              <div className="h-1 bg-white/10">
                <div className="h-full bg-[#F77F11] transition-all duration-300" style={{ width: `${((i + 1) / (steps.length + 1)) * 100}%` }} />
              </div>
            )}
            <div className="p-3 lg:p-4">
              <div className="flex items-center justify-between gap-2">
                <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#F77F11]">
                  {view === 'intro' ? (isEs ? 'Visita guiada' : 'Guided tour')
                    : view === 'final' ? (isEs ? 'Fin de la visita' : 'End of tour')
                      : `${isEs ? 'Visita guiada' : 'Guided tour'} · ${i + 1}/${steps.length}`}
                </p>
                <div className="flex items-center gap-1">
                  <button onClick={() => setView('min')} className="text-white/50 hover:text-white text-[11px] font-bold px-1.5" data-testid="tour-minimize">—</button>
                  <button onClick={dismiss} className="text-white/50 hover:text-white" data-testid="tour-close" aria-label="close"><X className="w-3.5 h-3.5" /></button>
                </div>
              </div>

              {view === 'intro' && (
                <>
                  <p className="mt-1.5 text-[13.5px] lg:text-[15px] font-bold">{isEs ? '¿Te la enseño en 1 minuto?' : 'Shall I show you around in 1 minute?'}</p>
                  <p className="mt-1 lg:mt-1.5 text-[11.5px] lg:text-[12.5px] text-white/75 leading-relaxed">
                    {isEs ? `${steps.length} paradas rápidas: qué ves, por qué importa y qué puedes tocar. ${isMobile ? 'Sal cuando quieras con la ✕.' : 'Sal cuando quieras con ESC.'}`
                      : `${steps.length} quick stops: what you see, why it matters and what you can try. ${isMobile ? 'Leave anytime with the ✕.' : 'Leave anytime with ESC.'}`}
                  </p>
                  <div className="mt-3 flex items-center gap-2">
                    <button onClick={() => go(0)} data-testid="tour-start"
                      className="inline-flex items-center gap-1 rounded-full bg-[#F77F11] px-4 py-1.5 text-[12px] font-bold text-white hover:opacity-90 transition-opacity">
                      {isEs ? 'Empezar' : 'Start'} <ChevronRight className="w-3.5 h-3.5" />
                    </button>
                    <button onClick={dismiss} data-testid="tour-explore-solo"
                      className="text-[12px] font-bold text-white/60 hover:text-white transition-colors">
                      {isEs ? 'Prefiero explorar solo' : 'I\u2019d rather explore alone'}
                    </button>
                  </div>
                </>
              )}

              {view === 'step' && (
                <>
                  <p className="mt-1.5 text-[13.5px] lg:text-[15px] font-bold">{s.title[isEs ? 0 : 1]}</p>
                  <p className="mt-1 lg:mt-1.5 text-[11.5px] lg:text-[12.5px] text-white/75 leading-relaxed" data-testid="tour-script">{isEs ? s.es : s.en}</p>
                  <div className="mt-2.5 lg:mt-3 flex items-center gap-2">
                    <button onClick={() => go(i - 1)} disabled={i === 0} data-testid="tour-prev"
                      className="inline-flex items-center gap-1 rounded-full border border-white/20 px-3 py-1.5 text-[12px] font-bold text-white/70 hover:text-white disabled:opacity-30 transition-colors">
                      <ChevronLeft className="w-3.5 h-3.5" /> {isEs ? 'Anterior' : 'Back'}
                    </button>
                    <button onClick={() => go(i + 1)} data-testid="tour-next"
                      className="inline-flex items-center gap-1 rounded-full bg-[#F77F11] px-4 py-1.5 text-[12px] font-bold text-white hover:opacity-90 transition-opacity">
                      {i < steps.length - 1 ? (isEs ? 'Siguiente' : 'Next') : (isEs ? 'Terminar' : 'Finish')} <ChevronRight className="w-3.5 h-3.5" />
                    </button>
                    <button onClick={dismiss} className="ml-auto hidden lg:inline text-[11px] text-white/40 hover:text-white/80 transition-colors" data-testid="tour-esc-hint">ESC</button>
                    <button onClick={dismiss} className="ml-auto lg:hidden text-[11px] font-bold text-white/40" data-testid="tour-exit-mobile">{isEs ? 'Salir' : 'Exit'}</button>
                  </div>
                </>
              )}

              {view === 'final' && (
                <>
                  <p className="mt-1.5 text-[13.5px] lg:text-[15px] font-bold">{isEs ? 'Esto, con TUS datos.' : 'This, with YOUR data.'}</p>
                  <p className="mt-1 lg:mt-1.5 text-[11.5px] lg:text-[12.5px] text-white/75 leading-relaxed">
                    {isEs ? 'Todo lo que has visto funcionaría igual con tu restaurante — en días tu OS está vivo. ¿Quieres que te contemos cómo?' : 'Everything you saw would run the same on your restaurant — your OS is alive in days. Want us to tell you how?'}
                  </p>
                  <div className="mt-3 flex flex-wrap items-center gap-2">
                    <button onClick={contactMe} disabled={asked} data-testid="tour-contact-me"
                      className="inline-flex items-center gap-1 rounded-full bg-[#F77F11] px-4 py-1.5 text-[12px] font-bold text-white hover:opacity-90 disabled:opacity-50 transition-opacity">
                      {asked ? '✓' : (isEs ? 'Quiero más información' : 'I want more info')}
                    </button>
                    <a href={`https://wa.me/14085901102?text=${encodeURIComponent(isEs ? 'Hola, acabo de ver la demo de Kitxens y quiero saber más' : 'Hi, I just saw the Kitxens demo and want to know more')}`}
                      target="_blank" rel="noopener noreferrer" data-testid="tour-whatsapp"
                      className="inline-flex items-center rounded-full bg-[#25D366] px-4 py-1.5 text-[12px] font-bold text-white hover:opacity-90 transition-opacity">
                      WhatsApp
                    </a>
                    <button onClick={dismiss} data-testid="tour-finish"
                      className="text-[12px] font-bold text-white/60 hover:text-white transition-colors">
                      {isEs ? 'Seguir explorando' : 'Keep exploring'}
                    </button>
                  </div>
                </>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
